import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import {
  Shield, RefreshCw, ExternalLink, Filter, Users, Search as SearchIcon,
  AlertTriangle, ClipboardList, Timer, X,
} from 'lucide-react';

const DEFAULT_LIMIT = 200;

const fmtTs = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const AdminSkiptraceAudit = () => {
  const token = localStorage.getItem('nexus_token');
  const [data, setData] = useState(null);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ user_id: '', tool_id: '', case_id: '', since: '' });

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      q.set('limit', String(DEFAULT_LIMIT));
      Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
      const [aRes, tRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/skip-trace/audit?${q.toString()}`, { headers }),
        fetch(`${BACKEND_URL}/api/skip-trace/tools`, { headers }),
      ]);
      if (aRes.status === 403) {
        toast({ title: 'Access denied', description: 'Admin access required.', variant: 'destructive' });
        setData({ rows: [], total: 0, summary: { by_user: {}, by_tool: {} }, active_lockouts: [] });
      } else if (aRes.ok) {
        setData(await aRes.json());
      }
      if (tRes.ok) setTools(await tRes.json());
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [filters, headers]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const clearLockout = async (userId, toolId) => {
    try {
      const q = toolId ? `?tool_id=${encodeURIComponent(toolId)}` : '';
      const r = await fetch(
        `${BACKEND_URL}/api/admin/skip-trace/lockouts/${encodeURIComponent(userId)}/clear${q}`,
        { method: 'POST', headers },
      );
      if (r.ok) {
        toast({ title: 'Lockout cleared' });
        fetchAudit();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const rows = data?.rows || [];
  const summary = data?.summary || { by_user: {}, by_tool: {} };
  const active_lockouts = data?.active_lockouts || [];
  const dailyLimit = data?.daily_limit || 20;

  return (
    <div className="space-y-6" data-testid="admin-skiptrace-audit">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 flex items-center justify-center rounded-lg" style={{ background: '#1D4ED8', boxShadow: '0 6px 14px -4px rgba(59,130,246,0.55)' }}>
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="nx-eyebrow">Admin · Compliance</div>
            <h1 className="nx-h1 mt-1">Skiptrace Audit</h1>
            <p className="nx-body mt-1">Every launch. Every user. Live rate-limit visibility across the agency.</p>
          </div>
        </div>
        <Button variant="outline" className="border-[#334155] text-[#94A3B8] hover:bg-[#182136]" onClick={fetchAudit} data-testid="refresh-audit">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-[#131A2A] border-[#334155]"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-[#94A3B8] text-xs uppercase tracking-wide"><ClipboardList className="w-4 h-4" /> Total Launches</div>
          <div className="text-3xl text-[#F8FAFC] font-semibold mt-2" data-testid="stat-total">{data?.total ?? 0}</div>
        </CardContent></Card>
        <Card className="bg-[#131A2A] border-[#334155]"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-[#94A3B8] text-xs uppercase tracking-wide"><Users className="w-4 h-4" /> Investigators</div>
          <div className="text-3xl text-[#F8FAFC] font-semibold mt-2">{Object.keys(summary.by_user || {}).length}</div>
        </CardContent></Card>
        <Card className="bg-[#131A2A] border-[#334155]"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-[#94A3B8] text-xs uppercase tracking-wide"><SearchIcon className="w-4 h-4" /> Tools Used</div>
          <div className="text-3xl text-[#F8FAFC] font-semibold mt-2">{Object.keys(summary.by_tool || {}).length}</div>
        </CardContent></Card>
        <Card className="bg-[#131A2A] border-[#334155]"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-amber-400 text-xs uppercase tracking-wide"><AlertTriangle className="w-4 h-4" /> Active Lockouts</div>
          <div className="text-3xl text-amber-300 font-semibold mt-2" data-testid="stat-lockouts">{active_lockouts.length}</div>
          <div className="text-[10px] text-[#94A3B8] mt-1">Daily limit {dailyLimit} · 2h lockout</div>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <Card className="bg-[#131A2A] border-[#334155]">
        <CardHeader className="pb-3"><CardTitle className="text-[#F8FAFC] text-base flex items-center gap-2"><Filter className="w-4 h-4" /> Filters</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input data-testid="filter-user-id" placeholder="User ID" value={filters.user_id}
                 onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
                 className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" />
          <Select value={filters.tool_id || 'all'} onValueChange={(v) => setFilters({ ...filters, tool_id: v === 'all' ? '' : v })}>
            <SelectTrigger data-testid="filter-tool" className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"><SelectValue placeholder="Tool" /></SelectTrigger>
            <SelectContent className="bg-[#131A2A] border-[#334155]">
              <SelectItem value="all">All tools</SelectItem>
              {tools.map((t) => <SelectItem key={t.id} value={t.id} className="text-[#F8FAFC]">{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="filter-case-id" placeholder="Case ID" value={filters.case_id}
                 onChange={(e) => setFilters({ ...filters, case_id: e.target.value })}
                 className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" />
          <Input data-testid="filter-since" type="datetime-local" value={filters.since}
                 onChange={(e) => setFilters({ ...filters, since: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                 className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" />
        </CardContent>
      </Card>

      {/* Active Lockouts */}
      {active_lockouts.length > 0 && (
        <Card className="bg-[#1A1206] border-amber-500/30">
          <CardHeader className="pb-3"><CardTitle className="text-amber-200 text-base flex items-center gap-2"><Timer className="w-4 h-4" /> Active Lockouts ({active_lockouts.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {active_lockouts.map((l, i) => (
                <div key={i} className="flex items-center justify-between rounded border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm">
                  <div>
                    <span className="text-amber-100 font-medium">{l.user_id}</span>
                    <span className="mx-2 text-amber-300/60">·</span>
                    <span className="text-amber-200">{l.tool_id}</span>
                    <span className="mx-2 text-amber-300/60">·</span>
                    <span className="text-amber-300/80 text-xs">expires {fmtTs(l.expires_at)}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => clearLockout(l.user_id, l.tool_id)}
                          className="border-amber-500/40 text-amber-100 hover:bg-amber-500/10"
                          data-testid={`clear-lockout-${l.user_id}-${l.tool_id}`}>
                    <X className="w-3.5 h-3.5 mr-1" /> Clear
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Audit table */}
      <Card className="bg-[#131A2A] border-[#334155]">
        <CardHeader className="pb-3"><CardTitle className="text-[#F8FAFC] text-base">Launch History</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="bg-[#0B0F19] text-[#94A3B8] uppercase text-xs">
                <tr>
                  <th className="text-left px-4 py-3">Time</th>
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3">Tool</th>
                  <th className="text-left px-4 py-3">Case</th>
                  <th className="text-right px-4 py-3">Link</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-[#334155] hover:bg-[#182136]">
                    <td className="px-4 py-3 text-[#CBD5E1]">{fmtTs(r.launched_at)}</td>
                    <td className="px-4 py-3">
                      <div className="text-[#F8FAFC]">{r.user_name || r.user_id}</div>
                      <div className="text-[10px] text-[#94A3B8] uppercase">{r.user_role || '—'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-[#F8FAFC]">{r.tool_name}</div>
                      <Badge className="mt-1 bg-[#182136] text-[#94A3B8] border border-[#334155] text-[10px]">{r.tool_type}</Badge>
                    </td>
                    <td className="px-4 py-3 text-[#CBD5E1]">{r.case_number || r.case_id || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      {r.tool_url ? (
                        <a href={r.tool_url} target="_blank" rel="noreferrer" className="text-[#3B82F6] inline-flex items-center gap-1">
                          <ExternalLink className="w-3.5 h-3.5" /> Open
                        </a>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && !loading && (
                  <tr><td colSpan={5} className="text-center px-4 py-10 text-[#94A3B8]">No launches match the current filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSkiptraceAudit;
